import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/stores/auth';
import { useBackHandler } from '@/lib/backButton';
import { openLegal, privacyFallback, privacyUrl, termsFallback, termsUrl } from '@/lib/legalLinks';
import { isStoreBuild } from '@/lib/platform';
import { selfDeleteShown } from '@/lib/selfDelete';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useIsMobile } from '@/hooks/useIsMobile';
import { mockApi } from '@/api/ieltsMock';
import { canAskIeltsProfile } from '@/lib/storeTabs';
import { vocabApi } from '@/api/vocab';
import { StudentTopBar } from '@/components/layout/StudentTopBar';
import { cn } from '@/lib/utils';
import { hapticsEnabled, setHapticsEnabled, tapHeavy, tapMedium, notifySuccess } from '@/lib/haptics';
import { failedCount, flush, onOutboxChange, onOutboxFailed, pendingCount } from '@/lib/outbox';
import { RecordQueueList } from './RecordQueue';
import { OFFLINE_RECORDS_ANCHOR, useOfflineRecords, useScrollToOfflineRecords } from '@/hooks/useOfflineRecords';
import { biometricsEnabled, checkBiometry, confirmIdentity, setBiometricsEnabled } from '@/lib/biometrics';
import {
  cancelDaily, loadPrefs, permissionGranted, requestPermission, savePrefs, scheduleDaily,
  type ReminderPrefs,
} from '@/lib/reminders';
import { useHasFeature } from '@/hooks/useHasFeature';
import { authApi } from '@/api/auth';
import { usersApi } from '@/api/users';
import { uploadsApi } from '@/api/uploads';
import { progressApi } from '@/api/progress';
import { cefrApi } from '@/api/cefr';
import { notificationsApi, type NotificationSettings } from '@/api/notifications';
import { setLanguage } from '@/app/i18n';
import { Card, CardTitle } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { Mascot } from '@/components/ui/Mascot';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Pill } from '@/components/ui/Pill';
import { humanizeError } from '@/lib/errors';
import { markUserLogout } from '@/lib/loginReturn';
import { disablePush } from '@/lib/push';
import { IS_APP_BUILD } from '@/lib/appBrand';
import type { CEFRLevel } from '@/api/courses';

const SKILLS = ['reading', 'listening', 'speaking', 'writing', 'grammar', 'vocabulary'] as const;
type Skill = (typeof SKILLS)[number];
const SKILL_LABEL: Record<Skill, string> = {
  reading: 'Reading',
  listening: 'Listening',
  speaking: 'Speaking',
  writing: 'Writing',
  grammar: 'Grammar',
  vocabulary: 'Vocabulary',
};

export function StudentProfilePage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const canCefr = useHasFeature('cefr');
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const stats = useQuery({ queryKey: ['student', 'stats'], queryFn: progressApi.getMyStats });
  // ⚠️ БЕЗ ДОСТУПА ЗАПРОСЫ НЕ ШЛЁМ (19.09.2026, договор `selling-words-guard.md` §8.3): карточки уровня
  // и навыков всё равно рисуются только при `canCefr`, а отказ по возможности — лишний 403 в трафике
  // (то, на что смотрел ревьюер Apple по 3.1.1). Сервер теперь отвечает 200, но запрос незачем.
  const skills = useQuery({ queryKey: ['student', 'skills'], queryFn: cefrApi.getSkills, enabled: canCefr });
  const level = useQuery({
    queryKey: ['student', 'cefr-level'],
    queryFn: cefrApi.getProgress,
    enabled: canCefr,
  });
  // Баллы мока, основной курс и словарь — для карточек по макету. Ключи те же,
  // что на других экранах: повторных запросов не будет.
  // В приложении — только при разделе тренажёров (lib/storeTabs.ts, `canAskIeltsProfile`); на сайте — всегда.
  const mockProfile = useQuery({
    queryKey: ['ielts', 'profile'],
    queryFn: mockApi.profile,
    enabled: canAskIeltsProfile(user),
  });
  const vocabStats = useQuery({ queryKey: ['vocab', 'stats'], queryFn: vocabApi.getStats });
  const marathons = useQuery({
    queryKey: ['student', 'enrolled-marathons'],
    queryFn: () => usersApi.myEnrolledMarathons(),
  });
  const primaryCourse = marathons.data?.find((m) => m.is_primary) ?? marathons.data?.[0];
  const marProgress = useQuery({
    queryKey: ['student', 'marathon-progress', primaryCourse?.id],
    queryFn: () => progressApi.getMyMarathonProgress(primaryCourse!.id),
    enabled: !!primaryCourse,
  });
  const lessonsDone = marProgress.data?.lessons_completed ?? 0;
  const vocabTotal = vocabStats.data?.total ?? 0;

  // Language preference: apply locally (i18n + localStorage) AND persist to the
  // backend (PUT /auth/me { lang }) so it survives device changes — TZ §5.12.
  const saveLang = useMutation({
    mutationFn: (lang: string) => authApi.updateMe({ lang }),
    onSuccess: (u) => {
      setUser(u);
      qc.invalidateQueries({ queryKey: ['auth'] });
    },
  });
  const onLangChange = (lang: 'kk' | 'ru' | 'en') => {
    setLanguage(lang);
    saveLang.mutate(lang);
  };

  // Avatar upload pipeline:
  //   1. POST to files-service (gets back { url })
  //   2. PUT /auth/me/avatar with that URL
  //   3. Update the auth store so the AppShell re-renders with the new image.
  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      const r = await uploadsApi.upload(file, 'avatars');
      const updated = await authApi.updateAvatar(r.url);
      return updated;
    },
    onSuccess: (u) => {
      setUser(u);
      qc.invalidateQueries({ queryKey: ['auth'] });
    },
  });

  if (isMobile) {
    return (
      <div className="space-y-3">
        {/* ── Шапка экрана: назад, название, шестерёнка ────────────────────
             В макете настройки — ОТДЕЛЬНЫЙ экран, а не стопка карточек внизу
             профиля. Здесь только шестерёнка, весь список — в `/app/profile/
             settings`. */}
        <div className="flex items-center gap-2.5 pb-1 pt-1.5">
          <Link
            to="/app"
            aria-label={t('nav.dashboard')}
            className="grid h-11 w-11 flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
          >
            <span className="material-symbols-outlined text-[18px]">chevron_left</span>
          </Link>
          <h1 className="flex-1 font-display text-[19px] font-bold text-on-surface">{t('nav.profile')}</h1>
          <Link
            to="/app/profile/settings"
            aria-label={t('profile.settingsTitle')}
            className="grid h-11 w-11 flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
          >
            <span className="material-symbols-outlined text-[18px]">settings</span>
          </Link>
        </div>

        {/* ── Шапка профиля: единственный оранжевый блок экрана ──────────── */}
        <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-primary to-primary-hover px-[18px] py-4 text-on-primary">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              aria-label={t('profile.changeAvatar')}
              className="relative grid h-14 w-14 flex-none place-items-center rounded-full bg-white font-display text-[19px] font-bold text-primary-pressed"
            >
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="h-14 w-14 rounded-full object-cover" />
              ) : (
                (user?.full_name ?? '?').slice(0, 2).toUpperCase()
              )}
              <span className="absolute -bottom-1 -right-1 grid h-[22px] w-[22px] place-items-center rounded-full bg-surface-container-low text-on-surface-variant">
                <span className="material-symbols-outlined text-[11px]">edit</span>
              </span>
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadAvatar.mutate(f);
                e.target.value = '';
              }}
            />
            {/* Отступ справа — под маскота: без него длинное имя уезжает ему за спину. */}
            <div className="min-w-0 flex-1 pr-16">
              <div className="truncate font-display text-[18px] font-bold leading-tight text-white">
                {user?.full_name}
              </div>
              <div className="truncate text-[11.5px] opacity-90">{user?.email}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {/* В приложении пустой уровень не рисуется прочерком, а роль — словом из перевода
                    (27.09.2026: на снимке для магазина стояли «—» и английское «student»). */}
                {level.data || !IS_APP_BUILD ? (
                  <span className="rounded-full bg-white px-2.5 py-1 text-[10.5px] font-bold text-primary-pressed">
                    {level.data ? t('profile.levelBadge', { level: level.data.current_level }) : '—'}
                  </span>
                ) : null}
                <span className="rounded-full bg-white/25 px-2.5 py-1 text-[10.5px] font-bold capitalize text-white">
                  {IS_APP_BUILD && user?.role ? t(`roles.${user.role}`, { defaultValue: user.role }) : user?.role}
                </span>
              </div>
            </div>
          </div>
          {/* Маскот в углу — место из макета (там заглушка «🦊»). */}
          <Mascot
            pose="hello"
            className="pointer-events-none absolute -bottom-2 -right-2 h-[104px] w-[92px] object-contain"
          />
          {uploadAvatar.error && (
            <p className="mt-2 text-[11.5px] text-white/90">{humanizeError(uploadAvatar.error)}</p>
          )}
        </div>

        {/* ── Три числа в ряд: очки, серия, рекорд ───────────────────────── */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { v: stats.data?.total_points ?? '—', l: t('profile.points'), warm: false },
            { v: stats.data?.current_streak ?? 0, l: t('profile.currentStreak'), warm: true },
            { v: stats.data?.longest_streak ?? 0, l: t('profile.bestStreak'), warm: false },
          ].map((it) => (
            <div
              key={it.l}
              className={cn(
                'rounded-[22px] px-3.5 py-3',
                it.warm ? 'bg-primary-container' : 'bg-surface-container-low',
              )}
            >
              <div
                className={cn(
                  'font-display text-[20px] font-bold',
                  it.warm ? 'text-on-primary-container' : 'text-on-surface',
                )}
              >
                {it.v}
              </div>
              <div
                className={cn(
                  'text-[10.5px]',
                  it.warm ? 'text-on-primary-container' : 'text-on-surface-variant',
                )}
              >
                {it.l}
              </div>
            </div>
          ))}
        </div>

        {/* ── Уровни по навыкам полосками ────────────────────────────────
             Цвет полоски зависит от уровня, как в макете: начальный — синий,
             дальше — оранжевый. Один цвет на всё сравнивать не давал.
             В приложении карточки нет (27.09.2026, сборка 61): CEFR-тест в store-сборку не входит,
             и ученик видел бы только «пока нет данных». Сайт без изменений. */}
        {IS_APP_BUILD ? null : (
        <div className="rounded-[26px] bg-surface-container-low px-4 py-3.5">
          <div className="flex items-center justify-between">
            <span className="text-[13.5px] font-semibold text-on-surface">{t('profile.cefrSkills')}</span>
            {level.data && (
              <span className="text-[10.5px] text-on-surface-variant">
                {level.data.current_level} → {level.data.next_level}
              </span>
            )}
          </div>
          {skills.data ? (
            <div className="mt-2 space-y-2.5">
              {SKILLS.map((sk) => {
                const row = skills.data![sk];
                const early = row.level === 'A1' || row.level === 'A2';
                const early1 = row.level === 'A1';
                return (
                  <div key={sk}>
                    <div className="flex items-center gap-2">
                      <span className="flex-1 text-[12.5px] font-semibold text-on-surface">
                        {SKILL_LABEL[sk]}
                      </span>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[10px] font-bold',
                          early1
                            ? 'bg-info-container text-info'
                            : 'bg-primary-container text-on-primary-container',
                        )}
                      >
                        {row.level}
                      </span>
                    </div>
                    <div className="mt-1 h-[5px] overflow-hidden rounded-full bg-outline">
                      <div
                        className={cn('h-full rounded-full', early1 ? 'bg-info' : 'bg-primary')}
                        style={{ width: `${Math.min(100, row.score)}%` }}
                      />
                    </div>
                    {!early && null}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-2 text-[12px] text-on-surface-variant">{t('profile.noSkillsData')}</p>
          )}
        </div>
        )}

      </div>
    );
  }


  /**
   * ⚠️ УРОВЕНЬ ПОКАЗЫВАЕМ ТОЛЬКО ПОСЛЕ МОК-ТЕСТА.
   *
   * Решение владельца 07.09.2026: «бейдж A1 смущает, ставим только после мока».
   * До мока никакого уровня у человека НЕТ — служебный прогресс по урокам
   * начинается с A1 у всех, и ученик читает это как приговор себе, ещё не
   * начав учиться.
   *
   * 08.09.2026 нашлось, что в профиле запрет не стоял вовсе: бейдж у имени
   * рисовался по уровню, а карточка навыков — по служебному прогрессу. Оба
   * места закрыты одним признаком.
   */
  const mockDone = !!mockProfile.data?.mock_done;
  const bands = mockProfile.data?.per_skill_band ?? {};
  const bandEntries = (['reading', 'listening', 'speaking', 'writing'] as const).filter((k) => bands[k] != null);

  return (
    <div>
      <StudentTopBar
        title={t('nav.profile')}
        subtitle={t('profile.subtitle')}
        streak={stats.data?.current_streak ?? 0}
        name={user?.full_name}
        avatarUrl={user?.avatar_url}
      />

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          {/* ── Кто я ───────────────────────────────────────────────────── */}
          <div className="relative flex items-start gap-5 overflow-hidden rounded-[24px] bg-primary px-[26px] py-6">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              aria-label={t('profile.changeAvatar')}
              className="relative flex-none rounded-full focus:outline-none"
            >
              <span className="grid h-[76px] w-[76px] place-items-center overflow-hidden rounded-full bg-on-primary/25 text-[24px] font-bold text-on-primary">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  (user?.full_name ?? '?')
                    .trim()
                    .split(/\s+/)
                    .map((w) => w[0] ?? '')
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()
                )}
              </span>
              {uploadAvatar.isPending && (
                <span className="absolute inset-0 grid place-items-center rounded-full bg-inverse-surface/60 text-inverse-on-surface">
                  <span className="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                </span>
              )}
              <span className="absolute -bottom-1 -right-1 rounded-full bg-white p-1 text-primary shadow-soft">
                <span className="material-symbols-outlined text-[14px]">photo_camera</span>
              </span>
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadAvatar.mutate(f);
                e.target.value = '';
              }}
            />
            <div className="min-w-0 flex-1">
              <h1 className="truncate font-display text-[24px] font-bold leading-[1.15] text-on-primary">
                {user?.full_name}
              </h1>
              {/* Даты регистрации у нас в профиле нет — в макете она есть,
                  но выдумывать её нельзя. */}
              <p className="mt-[5px] truncate text-[12.5px] text-on-primary/85">{user?.email}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {primaryCourse && (
                  <span className="rounded-full bg-on-primary/20 px-[13px] py-1.5 text-[11px] font-bold text-on-primary">
                    {primaryCourse.title}
                  </span>
                )}
                {mockDone && mockProfile.data?.level && (
                  <span className="rounded-full bg-white px-[13px] py-1.5 text-[11px] font-bold text-primary-pressed">
                    {mockProfile.data.level}
                  </span>
                )}
              </div>
              {uploadAvatar.error && <p className="mt-1 text-xs text-on-primary/90">{humanizeError(uploadAvatar.error)}</p>}
            </div>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="flex-none self-start rounded-full bg-on-primary/20 px-[17px] py-[9px] text-[11.5px] font-bold text-on-primary transition hover:bg-on-primary/30"
            >
              {t('common.edit')}
            </button>
          </div>

          {/* ── Счёт ────────────────────────────────────────────────────── */}
          <div className="mt-3.5 grid grid-cols-4 gap-[11px]">
            {[
              { v: lessonsDone, l: t('profile.lessonsDone'), c: 'text-on-surface' },
              { v: stats.data?.current_streak ?? 0, l: t('profile.currentStreak'), c: 'text-on-primary-container' },
              { v: vocabTotal, l: t('profile.words'), c: 'text-info' },
              { v: stats.data?.total_points ?? 0, l: t('profile.points'), c: 'text-on-surface' },
            ].map((x) => (
              <div key={x.l} className="rounded-[18px] bg-surface-container px-4 py-[15px]">
                <div className={cn('font-display text-[24px] font-bold tabular-nums', x.c)}>{x.v}</div>
                <div className="mt-0.5 text-[11px] text-on-surface-variant">{x.l}</div>
              </div>
            ))}
          </div>

          {/* ── Баллы по навыкам ────────────────────────────────────────
              Показываем ТОЛЬКО настоящие баллы мока. Пока мока нет — прежняя
              карточка уровней CEFR со своим приглашением пройти проверку. */}
          {bandEntries.length > 0 ? (
            <div className="mt-3.5 rounded-[22px] bg-surface-container px-[22px] py-5">
              {/* Балл мока — ПРИМЕРНЫЙ (владелец 20.10.2026): по верным ответам, не экзаменатор. */}
              <h2 className="mb-1 text-[15px] font-bold text-on-surface">{t('profile.approxBandTitle')}</h2>
              <p className="mb-[15px] text-[11.5px] text-on-surface-variant">{t('profile.approxBandNote')}</p>
              <div className="grid grid-cols-2 gap-x-[26px] gap-y-4">
                {bandEntries.map((k) => {
                  const band = bands[k]!;
                  // Полоса — доля от девяти баллов IELTS.
                  const pct = Math.max(0, Math.min(100, (band / 9) * 100));
                  const strong = band >= 6;
                  return (
                    <div key={k}>
                      <div className="flex items-center gap-3">
                        <span className="flex-1 text-[12.5px] font-semibold text-on-surface">
                          {t(`trainersHub.ielts${k[0].toUpperCase()}${k.slice(1)}`)}
                        </span>
                        <span className={cn('text-[12px] font-bold', strong ? 'text-on-primary-container' : 'text-info')}>
                          {band.toFixed(1)}
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-full bg-outline">
                        <div
                          className={cn('h-1.5 rounded-full transition-[width] duration-500', strong ? 'bg-primary' : 'bg-info')}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : IS_APP_BUILD ? null : (
            // ⚠️ Карточка CEFR — только сайт, как на телефоне (`profileAppClean.test.ts`): CEFR-теста в приложении
            // нет, и «Пока нет данных по навыкам» на широком экране (планшет Android) — пустое обещание.
            <section className="mt-3.5">
              <Card>
                <CardTitle>{t('profile.cefrSkills')}</CardTitle>
                {skills.isLoading ? (
                  <p className="mt-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
                ) : skills.data && mockDone ? (
                  <div className="mt-3 space-y-3">
                    {SKILLS.map((k) => (
                      <SkillBar key={k} label={SKILL_LABEL[k]} score={skills.data![k].score} level={skills.data![k].level} />
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-on-surface-variant">
                    {t('profile.noSkillsData')}{' '}
                    {canCefr && (
                      <Link to="/app/ielts/mock" className="font-semibold text-primary">
                        {t('profile.takeCefrTest')} →
                      </Link>
                    )}
                  </p>
                )}
              </Card>
            </section>
          )}
        </div>

        {/* ── Настройки ────────────────────────────────────────────────────
            ⚠️ КАРТОЧКИ «ТВОЙ ТАРИФ» ИЗ МАКЕТА ЗДЕСЬ НЕТ И НЕ БУДЕТ. В макете
            справа стоит «Your plan · Premium · Manage plan». Ровно такую
            карточку («Plan · 10 features enabled») проверяющий Apple приложил
            к отказу 26.08.2026. Ни тарифов, ни цен, ни управления подпиской в
            приложении быть не должно. */}
        <div className="space-y-3.5">
          <section>
            <h2 className="mx-0.5 mb-2.5 font-display text-[15.5px] font-bold text-on-surface">{t('profile.settings')}</h2>
            <LanguageCard onChange={onLangChange} saving={saveLang.isPending} />
          </section>
          <ChangePasswordCard />
          <NotificationSettingsCard />
          <SyncCard />
          {isStoreBuild() && <BiometricsCard />}
          {isStoreBuild() && <RemindersCard />}
          {isStoreBuild() && <OfflineRecordsCard />}
          {isStoreBuild() && <HapticsCard />}
          <DocumentsCard />
          <LogoutCard />
          <DeleteAccountCard />
        </div>
      </div>

      {editing && <EditProfileModal onClose={() => setEditing(false)} />}
    </div>
  );
}

/**
 * Документы — политика и условия на широком экране (02.10.2026).
 *
 * ⚠️ ЗАЧЕМ. Пункт «Документы» был только в настройках телефона (`MobileSettingsPage`); на широком экране
 * (планшет Android, сайт) вошедший ученик до политики не добирался никак. Адреса и способ открытия — те же, по
 * площадке, из `lib/legalLinks.ts`: iOS-приложение — Google Docs системным окном, Android и сайт — свои страницы.
 */
function DocumentsCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rows = [
    { key: 'privacy', label: t('profile.privacyPolicy'), open: () => openLegal(privacyUrl(), navigate, privacyFallback()) },
    { key: 'terms', label: t('profile.termsOfUse'), open: () => openLegal(termsUrl(), navigate, termsFallback()) },
  ];
  return (
    <Card>
      <CardTitle>{t('profile.documentsSection')}</CardTitle>
      <div className="mt-3 space-y-1">
        {rows.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => void r.open()}
            className="flex w-full items-center justify-between gap-3 rounded-button px-1 py-2.5 text-left text-sm font-semibold text-on-surface transition hover:bg-surface-container"
          >
            {r.label}
            <span className="material-symbols-outlined text-[18px] text-on-surface-variant">chevron_right</span>
          </button>
        ))}
      </div>
    </Card>
  );
}

/**
 * Выход из аккаунта.
 *
 * ЗАЧЕМ ОТДЕЛЬНОЙ КАРТОЧКОЙ. Кнопка выхода жила только в боковом меню, а оно
 * появляется на широком экране. На телефоне выйти было НЕЧЕМ: ни в оболочке, ни
 * в профиле. Пока всё работает, это незаметно; но стоит сессии испортиться — и
 * человек заперт в приложении, которое показывает ошибки и ничего не даёт
 * сделать. Ровно это и случилось 22.08.2026.
 *
 * Токен продления гасим на сервере, но результата не ждём: даже без сети выход
 * должен состояться, иначе «выйти» в самолёте не сработает.
 */
/**
 * Язык интерфейса — строкой с тремя таблетками, как в макете.
 *
 * Выбор сохраняется НА СЕРВЕРЕ, а не только в браузере: иначе на телефоне и на
 * компьютере у одного человека были бы разные языки.
 */
function LanguageCard({ onChange, saving }: { onChange: (l: 'kk' | 'ru' | 'en') => void; saving: boolean }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="rounded-[20px] bg-surface-container px-[18px] py-3.5">
      <div className="flex items-center gap-3">
        <span className="flex-1 text-[13px] font-semibold text-on-surface">{t('profile.interfaceLang')}</span>
        <span className="flex flex-none gap-[3px] rounded-full bg-background p-[3px]">
          {(['kk', 'ru', 'en'] as const).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => onChange(l)}
              className={cn(
                'rounded-full px-[11px] py-1.5 text-[10px] font-bold transition',
                i18n.language === l ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:bg-surface-container',
              )}
            >
              {l === 'kk' ? 'ҚАЗ' : l === 'ru' ? 'РУС' : 'ENG'}
            </button>
          ))}
        </span>
      </div>
      {saving && <p className="mt-1 text-[11px] text-on-surface-variant">{t('profile.saving')}</p>}
    </div>
  );
}

function LogoutCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const refreshToken = useAuthStore((s) => s.refreshToken);

  const leave = async () => {
    tapHeavy();
    // Метку устройства — до сброса сессии и с ожиданием (lib/push.ts); на сайте это ничего не делает.
    await disablePush();
    if (refreshToken) authApi.logout(refreshToken).catch(() => {});
    // Вышел сам — без `?next` (loginReturn.ts, 27.09.2026).
    markUserLogout();
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <Card>
      <CardTitle>{t('profile.sessionTitle')}</CardTitle>
      <p className="mt-1 text-sm text-on-surface-variant">{t('profile.sessionDesc')}</p>
      <Button variant="secondary" className="mt-4" onClick={() => void leave()}>
        <span className="material-symbols-outlined text-[20px]">logout</span>
        {t('common.logout')}
      </Button>
    </Card>
  );
}

// DeleteAccountCard — self-service account deletion (App Store / Google Play).
// Requires an explicit acknowledgement checkbox before the destructive action is
// enabled. On success the local session is cleared and the user is sent to login.
// The backend soft-deletes (status "deleted"): the account becomes inactive and
// can no longer sign in, but the row is retained.
//
// ⚠️ НА САЙТЕ СКРЫТА, В ПРИЛОЖЕНИИ ИЗ МАГАЗИНА — ВСЕГДА (D37, 12.09.2026): правило
// и причины — `lib/selfDelete.ts`. Условие здесь, а не у места вызова: тогда его
// не обойти новым местом вызова. Экспортирована ради `selfDeleteHidden.test.ts`.
export function DeleteAccountCard() {
  // Настройки с сервера приезжают после первой отрисовки — без подписки кнопка
  // осталась бы на умолчании.
  useAppConfig();
  if (!selfDeleteShown()) return null;
  return <DeleteAccountCardBody />;
}

function DeleteAccountCardBody() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const [confirming, setConfirming] = useState(false);

  const del = useMutation({
    mutationFn: async () => {
      tapHeavy();
      // Метку устройства — до удаления: удаление учётки на сервере метки не чистит (договор push.md §8).
      await disablePush();
      return usersApi.deleteMyAccount();
    },
    onSuccess: () => {
      markUserLogout();
      logout();
      navigate('/login', { replace: true });
    },
  });

  return (
    <Card className="border border-error/25 bg-error-container/20">
      <CardTitle className="text-error">{t('profile.deleteTitle')}</CardTitle>
      <p className="mt-2 text-sm text-on-surface-variant">{t('profile.deleteDesc')}</p>
      <Button
        variant="danger"
        className="mt-4"
        onClick={() => {
          del.reset();
          setConfirming(true);
        }}
      >
        <span className="material-symbols-outlined text-[20px]">delete_forever</span>
        {t('profile.deleteBtn')}
      </Button>

      <Modal open={confirming} onClose={() => setConfirming(false)} title={t('profile.deleteConfirmTitle')} size="sm">
        {/* ⚠️ Без галочки «понимаю, что необратимо» (D71, 25.09.2026): удаление мягкое, учётку
            возвращает администратор — галочка утверждала неправду. Текст окна утверждён владельцем. */}
        <p className="text-sm text-on-surface-variant">{t('profile.deleteConfirmDesc')}</p>
        {del.error && <p className="mt-3 text-sm text-error">{humanizeError(del.error)}</p>}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirming(false)} disabled={del.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" loading={del.isPending} onClick={() => del.mutate()}>
            {t('profile.deleteConfirmBtn')}
          </Button>
        </div>
      </Modal>
    </Card>
  );
}


function SkillBar({ label, score, level }: { label: string; score: number; level: CEFRLevel }) {
  return (
    <li>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-semibold text-on-surface">{label}</span>
        <Pill variant="primary">{level}</Pill>
      </div>
      <div className="h-3 overflow-hidden rounded-pill bg-surface-container-highest">
        <div className="h-full bg-primary" style={{ width: `${score}%` }} />
      </div>
    </li>
  );
}

// ChangePasswordCard — wraps PUT /auth/password/update (authApi.updatePassword).
// Mirrors the curator profile's password flow so both roles behave identically.
export function ChangePasswordCard() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  // Пароля нет у тех, кто зарегистрировался по коду из письма. Им нечего вводить
  // в поле «текущий пароль», и предлагать «сменить» — издевательство: заказчик
  // 22.08.2026 спросил ровно это, «в чём прикол change password».
  // Поле может отсутствовать (старый ответ сервера) — тогда ведём себя как
  // раньше и показываем смену.
  const hasPassword = user?.password_set !== false;
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () =>
      hasPassword
        ? authApi.updatePassword({ old_password: oldPass, new_password: newPass })
        : authApi.setPassword(newPass),
    onSuccess: () => {
      setErr(null);
      setOldPass('');
      setNewPass('');
      setConfirmPass('');
      // Пароль появился — карточка должна превратиться в «смену», иначе человек
      // задаст его дважды и решит, что первый раз не сработал.
      if (!hasPassword && user) setUser({ ...user, password_set: true });
    },
    onError: (e: unknown) => setErr(humanizeError(e)),
  });

  const submit = () => {
    if (newPass.length < 8) {
      setErr(t('profile.passwordTooShort'));
      return;
    }
    if (newPass !== confirmPass) {
      setErr(t('profile.passwordMismatch'));
      return;
    }
    change.mutate();
  };

  return (
    <Card>
      <CardTitle>{hasPassword ? t('profile.changePassword') : t('profile.setPassword')}</CardTitle>
      {!hasPassword && (
        <p className="mt-1 text-sm text-on-surface-variant">{t('profile.setPasswordDesc')}</p>
      )}
      <div className="mt-4 grid gap-3">
        {hasPassword && (
          <Input
            type="password"
            label={t('profile.currentPassword')}
            value={oldPass}
            onChange={(e) => {
              setOldPass(e.target.value);
              setErr(null);
            }}
          />
        )}
        <Input
          type="password"
          label={t('profile.newPassword')}
          hint={t('profile.passwordHint')}
          value={newPass}
          onChange={(e) => {
            setNewPass(e.target.value);
            setErr(null);
          }}
        />
        <Input
          type="password"
          label={t('profile.confirmNewPassword')}
          value={confirmPass}
          onChange={(e) => {
            setConfirmPass(e.target.value);
            setErr(null);
          }}
        />
        {err && <p className="text-xs text-error">{err}</p>}
        {change.isSuccess && !err && (
          <p className="text-xs text-success">✓ {t('profile.passwordChanged')}</p>
        )}
        <div className="flex justify-end pt-2">
          <Button
            onClick={submit}
            loading={change.isPending}
            disabled={(hasPassword && !oldPass) || !newPass}
          >
            {hasPassword ? t('profile.changePasswordAction') : t('profile.setPasswordAction')}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// CHANNEL_LABELS are now resolved via t() inside the component.

// NotificationSettingsCard — UI ONLY. Reads/writes via notification-service's
// API module (no backend in this vertical). TZ §5.12 "Настройки уведомлений".
function NotificationSettingsCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // ⚠️ Без права chat нет и тумблера «Сообщения в чате» (14.09.2026): чата у такого ученика нет.
  const canChat = useHasFeature('chat');
  const channelLabels: Partial<Partial<Record<keyof NotificationSettings['channels'], string>>> = {
    homework: t('profile.notifHomework'),
    // В приложении расписания нет — «Уроки» без «и расписание» (27.09.2026, техдолг 319).
    lessons: t(IS_APP_BUILD ? 'profile.notifLessonsApp' : 'profile.notifLessons'),
    // ⚠️ Переключателя «Платежи» здесь больше нет ВОВСЕ (01.09.2026). Раньше он
    // прятался только в магазинной сборке, а на сайте ученик его видел — и
    // проверяющий, заходящий туда тем же демо-доступом, тоже.
    // ⚠️ В ПРИЛОЖЕНИИ — КАК В НАСТРОЙКАХ ТЕЛЕФОНА (`MobileSettingsPage`, 02.10.2026, сверка пути бесплатника Ш4):
    // разговорных комнат и чата там нет — нет и их переключателей. Широкую ветку видит планшет Android.
    ...(IS_APP_BUILD ? {} : { speaking: t('profile.notifSpeaking') }),
    ...(canChat && !IS_APP_BUILD ? { chat: t('profile.notifChat') } : {}),
  };
  const settings = useQuery({
    queryKey: ['notifications', 'settings'],
    queryFn: notificationsApi.getSettings,
  });

  const save = useMutation({
    mutationFn: (next: NotificationSettings) => notificationsApi.updateSettings(next),
    onSuccess: (saved) => {
      qc.setQueryData(['notifications', 'settings'], saved);
    },
  });

  const s = settings.data;

  const toggle = (patch: Partial<NotificationSettings>) => {
    if (!s) return;
    save.mutate({ ...s, ...patch });
  };
  const toggleChannel = (key: keyof NotificationSettings['channels']) => {
    if (!s) return;
    save.mutate({ ...s, channels: { ...s.channels, [key]: !s.channels[key] } });
  };

  return (
    <Card>
      <CardTitle>{t('profile.notifications')}</CardTitle>
      {settings.isLoading || !s ? (
        <p className="mt-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
      ) : (
        <div className="mt-4 space-y-2">
          <ToggleRow
            label={t('profile.emailNotifications')}
            checked={s.email_enabled}
            onChange={() => toggle({ email_enabled: !s.email_enabled })}
          />
          {/* В приложении пуши включаются системным окном и своим блоком настроек телефона — эта строка только сайт. */}
          {IS_APP_BUILD ? null : (
            <ToggleRow
              label={t('profile.pushNotifications')}
              checked={s.push_enabled}
              onChange={() => toggle({ push_enabled: !s.push_enabled })}
            />
          )}
          <div className="my-3 border-t border-outline-variant" />
          {/* ⚠️ В приложении из магазина канал «Платежи» НЕ показываем — как и в
              мобильных настройках. Оплата у нас офлайн, в школе: в приложении
              нет ни цен, ни покупок. Тумблер с таким названием намекает
              проверяющему на оплату мимо Apple, то есть ровно на то, чего мы
              избегаем. Сама настройка остаётся — скрыт только вид. */}
          {(Object.keys(channelLabels) as (keyof NotificationSettings['channels'])[])

            .map(
            (key) => (
              <ToggleRow
                key={key}
                label={channelLabels[key] ?? key}
                checked={s.channels[key]}
                onChange={() => toggleChannel(key)}
              />
            ),
          )}
          {save.error && (
            <p className="text-xs text-error">{humanizeError(save.error)}</p>
          )}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BiometricsCard — вход по лицу или отпечатку.
//
// Перед включением просим подтвердить личность: иначе человек включил бы защиту,
// не проверив, что она вообще срабатывает на его устройстве, и обнаружил бы это
// только при следующем запуске — уже будучи запертым снаружи.
function BiometricsCard() {
  const { t } = useTranslation();
  const [on, setOn] = useState(biometricsEnabled);
  const [available, setAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    void checkBiometry().then((r) => setAvailable(r.available));
  }, []);

  const toggle = async () => {
    if (!on) {
      const passed = await confirmIdentity(t('biometrics.reason'), t('biometrics.usePassword'));
      if (!passed) return; // не включаем то, что не сработало
    }
    const next = !on;
    setBiometricsEnabled(next);
    setOn(next);
  };

  return (
    <Card>
      <CardTitle>{t('biometrics.title')}</CardTitle>
      <p className="mt-1 text-sm text-on-surface-variant">{t('biometrics.desc')}</p>
      {available === false ? (
        <p className="mt-3 text-sm text-on-surface-variant">{t('biometrics.unavailable')}</p>
      ) : (
        <div className="mt-3">
          <ToggleRow label={t('biometrics.title')} checked={on} onChange={() => void toggle()} />
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RemindersCard — локальные напоминания.
//
// Ежедневное напоминание с выбором времени стоит первым не случайно: оно
// работает у любого человека, даже без записи на марафон и без расписания.
// Поэтому проверяющий в магазине гарантированно может его включить и убедиться,
// что уведомления приходят — а заодно это честно полезная привычка.
//
// Разрешение спрашиваем в момент включения, а не при запуске приложения:
// системный запрос без причины чаще получает отказ, и второй раз его не покажешь.
function RemindersCard() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<ReminderPrefs>(loadPrefs);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    void permissionGranted().then((ok) => setDenied(!ok && prefs.daily));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = async (next: ReminderPrefs) => {
    if (next.daily && !prefs.daily) {
      const ok = await requestPermission();
      if (!ok) {
        setDenied(true);
        return; // не включаем то, что всё равно не сработает
      }
      setDenied(false);
    }
    setPrefs(next);
    savePrefs(next);
    if (next.daily) {
      await scheduleDaily(next.dailyTime, t('reminders.dailyTitle'), t('reminders.dailyBody'));
    } else {
      await cancelDaily();
    }
  };

  return (
    <Card>
      <CardTitle>{t('reminders.title')}</CardTitle>
      <p className="mt-1 text-sm text-on-surface-variant">{t('reminders.desc')}</p>

      <div className="mt-3 space-y-1">
        <ToggleRow
          label={t('reminders.daily')}
          checked={prefs.daily}
          onChange={() => void update({ ...prefs, daily: !prefs.daily })}
        />
        {prefs.daily && (
          <label className="flex items-center justify-between py-1">
            <span className="text-sm text-on-surface">{t('reminders.dailyTime')}</span>
            <input
              type="time"
              value={prefs.dailyTime}
              onChange={(e) => void update({ ...prefs, dailyTime: e.target.value })}
              className="rounded-button border border-outline-variant bg-surface px-3 py-1.5 text-sm text-on-surface"
            />
          </label>
        )}
        {/* «За час до занятия» — только сайт (27.09.2026): в приложении расписания нет. */}
        {IS_APP_BUILD ? null : (
          <ToggleRow
            label={t('reminders.lessons')}
            checked={prefs.lessons}
            onChange={() => void update({ ...prefs, lessons: !prefs.lessons })}
          />
        )}
        <ToggleRow
          label={t('reminders.homework')}
          checked={prefs.homework}
          onChange={() => void update({ ...prefs, homework: !prefs.homework })}
        />
      </div>

      {denied && <p className="mt-3 text-sm text-error">{t('reminders.denied')}</p>}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SyncCard — ответы, не дошедшие до сервера.
//
// Обычно очередь разгружается сама: при появлении связи и при запуске приложения.
// Кнопка здесь на случай, когда автоматика почему-то не сработала, и — что важнее —
// чтобы ученик ВИДЕЛ, что часть работы ещё не ушла. Раньше он этого не знал:
// приложение показывало галочку «сохранено» независимо от того, дошёл ответ или нет.
function SyncCard() {
  const { t } = useTranslation();
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);
  // Отказанное сервером — тоже не отправлено: без него карточка писала «Всё отправлено» рядом с красной
  // полосой «не отправлено: 1» (02.10.2026, дыра 5 сверки).
  const [failed, setFailed] = useState(0);

  useEffect(() => {
    void pendingCount().then(setPending);
    void failedCount().then(setFailed);
    const offChange = onOutboxChange(setPending);
    const offFailed = onOutboxFailed(setFailed);
    return () => {
      offChange();
      offFailed();
    };
  }, []);

  const sync = async () => {
    setBusy(true);
    try {
      await flush();
      const left = await pendingCount();
      if (left === 0) notifySuccess();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardTitle>{t('profile.syncTitle')}</CardTitle>
      <p className="mt-1 text-sm text-on-surface-variant">{t('profile.syncDesc')}</p>
      <div className="mt-3 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-on-surface">
          {pending > 0
            ? t('net.pending', { count: pending })
            : failed > 0
              ? t('net.failed', { count: failed })
              : t('profile.syncDone')}
        </span>
        <Button size="sm" variant="secondary" disabled={busy || pending === 0} onClick={sync}>
          {t('profile.syncNow')}
        </Button>
      </div>
      {/* Неотправленные записи голоса списком — тот же, что экраном на телефоне (промпт 22, 18.10.2026). Нет их —
          список ничего не рисует, карточка остаётся счётчиком. */}
      <div className="mt-4">
        <RecordQueueList />
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OfflineRecordsCard — тумблер «Запись без связи» на широком экране (планшет): тот же, что строкой в
// настройках телефона. Решение владельца 02.10.2026: только приложение, по умолчанию выключено; условия
// показа — в `useOfflineRecords`.
function OfflineRecordsCard() {
  const { t } = useTranslation();
  const offline = useOfflineRecords();
  useScrollToOfflineRecords(offline.visible);
  if (!offline.visible) return null;
  return (
    <div id={OFFLINE_RECORDS_ANCHOR}>
      <Card>
        <CardTitle>{t('profile.offlineRecordsTitle')}</CardTitle>
        <p className="mt-1 text-sm text-on-surface-variant">{t('profile.offlineRecordsDesc')}</p>
        <div className="mt-3">
          <ToggleRow label={t('profile.offlineRecordsTitle')} checked={offline.on} onChange={offline.toggle} />
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HapticsCard — выключатель тактильного отклика.
//
// Часть людей вибрацию не переносит, и отказ должен стоить один тап, а не поход
// в системные настройки телефона. Настройка живёт в localStorage: отклик
// срабатывает прямо в обработчиках нажатий, и ждать асинхронное хранилище
// нельзя — вибрация опоздает и потеряет смысл.
//
// При включении сразу даём почувствовать, что именно включилось.
function HapticsCard() {
  const { t } = useTranslation();
  const [on, setOn] = useState(hapticsEnabled);

  const toggle = () => {
    const next = !on;
    setHapticsEnabled(next);
    setOn(next);
    if (next) tapMedium();
  };

  return (
    <Card>
      <CardTitle>{t('profile.hapticsTitle')}</CardTitle>
      <p className="mt-1 text-sm text-on-surface-variant">{t('profile.hapticsDesc')}</p>
      <div className="mt-3">
        <ToggleRow label={t('profile.hapticsTitle')} checked={on} onChange={toggle} />
      </div>
    </Card>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between py-1">
      <span className="text-sm text-on-surface">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={onChange}
        className={`relative h-6 w-11 shrink-0 rounded-pill transition-colors ${
          checked ? 'bg-primary' : 'bg-surface-container-highest'
        }`}
      >
        {/* ⚠️ `left-0` ОБЯЗАТЕЛЕН. У кнопки содержимое центрируется браузером,
            и у абсолютной метки без `left` начало отсчёта — СЕРЕДИНА кнопки.
            Сдвиг прибавлялся к ней, и кружок уезжал за правый край: замерено
            02.09.2026 — 44 точки от края вместо 22. Заметно это стало, когда
            кабинет стал песочным: раньше белый кружок терялся на белой
            карточке. */}
        <span
          className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow-soft transition-transform ${
            checked ? 'translate-x-[1.375rem]' : 'translate-x-0.5'
          }`}
        />
      </button>
    </label>
  );
}

export function EditProfileModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();
  const [fullName, setFullName] = useState(user?.full_name ?? '');

  // Самодельное окно, не `Modal`: «Назад» на Android уводила на главную, не закрыв его (02.10.2026).
  // На iPhone профиль — корень вкладки, жеста там нет.
  const closeOnBack = useCallback(() => {
    onClose();
    return true;
  }, [onClose]);
  useBackHandler(closeOnBack);

  const save = useMutation({
    mutationFn: (full_name: string) => authApi.updateMe({ full_name }),
    onSuccess: (u) => {
      setUser(u);
      qc.invalidateQueries({ queryKey: ['auth'] });
      onClose();
    },
  });

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center bg-inverse-surface/40 p-4">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-card bg-surface-container-lowest p-6 shadow-ambient">
        <h2 className="mb-4 font-display text-xl font-bold tracking-display">{t('profile.editProfileTitle')}</h2>
        <div className="space-y-3">
          <Input
            label={t('profile.fullName')}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <Input label={t('auth.email')} disabled defaultValue={user?.email} hint={t('profile.emailAdminOnly')} />
          {save.error && (
            <p className="text-xs text-error">{humanizeError(save.error)}</p>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>{t('common.cancel')}</Button>
          <Button
            onClick={() => save.mutate(fullName.trim())}
            disabled={save.isPending || !fullName.trim() || fullName.trim() === user?.full_name}
          >
            {save.isPending ? t('profile.saving') : t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}

